import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import api, { apiError, fmtMXN, fmtDate, BACKEND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Receipt, Plus, FileText, Upload, Send, ChevronDown, ChevronUp, CheckCircle2, Trash2, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";

const ESTATUS = {
  por_facturar: { label: "Por facturar", variant: "secondary" },
  facturado_parcial: { label: "Facturado parcialmente", variant: "default" },
  facturado: { label: "Facturado por completo (pend. pago)", variant: "default" },
  pagado: { label: "Pagado / Cerrado", variant: "outline" },
};
const INV_ESTATUS = { borrador: "Borrador", enviada: "Enviada (pend. pago)", pagada: "Pagada" };

const BSTATUS = [["por_facturar", "Por facturar"], ["facturado_parcial", "Facturado parcialmente"], ["facturado", "Facturado por completo"], ["pagado", "Pagado / Cerrado"]];
const COLUMNS = [
  { key: "_x", type: "none" },
  { key: "folio", label: "Pedido", accessor: (o) => o.folio, placeholder: "Pedido" },
  { key: "cliente", label: "Cliente", accessor: (o) => o.client_snapshot?.razon_social, placeholder: "Cliente" },
  { key: "total", label: "Total", accessor: (o) => o.total, placeholder: "Total" },
  { key: "facturado", label: "Facturado", accessor: (o) => o.facturado, placeholder: "Facturado" },
  { key: "saldo", label: "Saldo", accessor: (o) => o.saldo_facturar, placeholder: "Saldo" },
  { key: "estatus", label: "Estatus", type: "select", accessor: (o) => o.factura_estatus, options: BSTATUS.map(([value, label]) => ({ value, label })) },
  { key: "_a", type: "none" },
];

export default function Billing() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [cat, setCat] = useState({ formas_pago: [], usos_cfdi: [], metodos_pago: [] });
  const [dlgOrder, setDlgOrder] = useState(null);
  const [form, setForm] = useState({ metodo_pago: "PUE", forma_pago: "03", uso_cfdi: "G03", concepto: "", is_partial: false, lineas: [] });

  const load = () => api.get("/billing/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); api.get("/sat-catalogs").then((r) => setCat(r.data)); }, []);
  const tools = useTableTools(orders, COLUMNS);

  const [params] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const oid = params.get("order");
    if (!oid || !orders.length || !orders.find((o) => o.id === oid)) return;
    setExpanded(oid);
    setHighlightId(oid);
    setTimeout(() => document.getElementById(`billing-row-${oid}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    const t = setTimeout(() => setHighlightId(null), 2800);
    return () => clearTimeout(t);
  }, [orders, params]);

  const openCreate = (o) => { setForm({ metodo_pago: "PUE", forma_pago: "03", uso_cfdi: "G03", concepto: "", is_partial: false, lineas: (o.lineas || []).map((l) => ({ ...l, cantidad_fac: 0 })) }); setDlgOrder(o); };
  const setLine = (idx, v) => setForm((f) => ({ ...f, lineas: f.lineas.map((l, i) => (i === idx ? { ...l, cantidad_fac: v } : l)) }));
  const calcMonto = () => (form.lineas || []).reduce((s, l) => { const q = form.is_partial ? Number(l.cantidad_fac || 0) : l.restante; return s + q * l.precio_unitario * (l.impuesto === "iva16" ? 1.16 : 1); }, 0);
  const createInvoice = async () => {
    const partial = form.is_partial;
    const items = (form.lineas || []).filter((l) => (partial ? Number(l.cantidad_fac) > 0 : l.restante > 0)).map((l) => ({ sku: l.sku, nombre: l.nombre, cantidad: partial ? Number(l.cantidad_fac) : l.restante, precio_unitario: l.precio_unitario, impuesto: l.impuesto }));
    if (partial && items.length === 0) return toast.error("Ingresa la cantidad a facturar en al menos un ítem");
    try {
      await api.post(`/orders/${dlgOrder.id}/invoices`, { metodo_pago: form.metodo_pago, forma_pago: form.forma_pago, uso_cfdi: form.uso_cfdi, concepto: form.concepto, is_partial: partial, items: partial ? items : null });
      toast.success("Factura creada (borrador)"); setDlgOrder(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadAndAttach = async (inv, kind, file, endpoint) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/sales-invoices/${inv.id}/${endpoint}`, { kind, file_id: up.data.file_id });
      toast.success(`${kind.toUpperCase()} adjuntado`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendInvoice = async (inv) => {
    try { const r = await api.post(`/sales-invoices/${inv.id}/send`); toast.success(`Factura enviada a ${r.data.to} (${r.data.email_status}). Vence ${fmtDate(r.data.fecha_vencimiento)}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendComplemento = async (inv) => {
    try { const r = await api.post(`/sales-invoices/${inv.id}/send-complemento`); toast.success(`Complemento enviado a ${r.data.to} (${r.data.email_status})`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const markPaid = async (inv) => {
    try { await api.post(`/sales-invoices/${inv.id}/mark-paid`); toast.success("Factura marcada como pagada / cerrada (PUE)"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delInvoice = async (inv) => { try { await api.delete(`/sales-invoices/${inv.id}`); toast.success("Eliminada"); load(); } catch (e) { toast.error(apiError(e)); } };
  const preview = async (inv) => { try { const r = await api.get(`/sales-invoices/${inv.id}/preview`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiError(e)); } };

  const FileBtn = ({ inv, kind, endpoint, label, done }) => (
    <label className={`inline-flex items-center gap-1.5 cursor-pointer border rounded px-2.5 py-1.5 text-xs hover:bg-muted ${done ? "border-emerald-400 text-emerald-700" : ""}`} data-testid={`${endpoint}-${kind}-${inv.id}`}>
      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />} {label}
      <input type="file" className="hidden" accept={kind === "pdf" ? ".pdf" : ".xml"} onChange={(e) => { uploadAndAttach(inv, kind, e.target.files[0], endpoint); e.target.value = ""; }} />
    </label>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2"><Receipt className="w-7 h-7" /> Órdenes a Facturar</h1><p className="text-muted-foreground text-sm">Control de facturación: emisión, envío al cliente y cierre de pago (complemento SAT).</p></div>
        <TableToolbar tools={tools} columns={COLUMNS} filename="ordenes-a-facturar" />
      </div>

      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
            {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
          </TableHeader>
          <TableBody>
            {tools.view.map((o) => (
              <Fragment key={o.id}>
                <TableRow id={`billing-row-${o.id}`} data-testid="billing-row" className={`cursor-pointer ${highlightId === o.id ? "flash-row" : ""}`} onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  <TableCell>{expanded === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</TableCell>
                  <TableCell className="font-mono font-medium">{o.folio}</TableCell>
                  <TableCell>{o.client_snapshot?.razon_social}</TableCell>
                  <TableCell>{fmtMXN(o.total)}</TableCell>
                  <TableCell>{fmtMXN(o.facturado)}</TableCell>
                  <TableCell className={o.saldo_facturar > 0.01 ? "text-amber-600 font-medium" : "text-emerald-600"}>{fmtMXN(o.saldo_facturar)}</TableCell>
                  <TableCell><Badge variant={ESTATUS[o.factura_estatus]?.variant || "secondary"}>{ESTATUS[o.factura_estatus]?.label || o.factura_estatus}</Badge></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {o.saldo_facturar > 0.01 && <Button size="sm" onClick={() => openCreate(o)} data-testid={`create-invoice-${o.id}`}><Plus className="w-4 h-4 mr-1" /> Crear factura</Button>}
                  </TableCell>
                </TableRow>
                {expanded === o.id && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-muted/30 p-4">
                      {o.facturas.length === 0 && <p className="text-sm text-muted-foreground">Sin facturas. Usa "Crear factura" para iniciar (puedes hacer facturación parcial para rentas mensuales).</p>}
                      <div className="space-y-3">
                        {o.facturas.map((inv) => (
                          <div key={inv.id} className="border rounded-lg p-3 bg-background" data-testid="invoice-card">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div>
                                <span className="font-mono font-semibold">{inv.folio_interno}</span>
                                <Badge className="ml-2" variant={inv.estatus === "pagada" ? "outline" : inv.estatus === "enviada" ? "default" : "secondary"}>{INV_ESTATUS[inv.estatus]}</Badge>
                                {inv.is_partial && <Badge variant="secondary" className="ml-1">Parcial</Badge>}
                                <div className="text-xs text-muted-foreground mt-1">{fmtMXN(inv.monto)} · {inv.metodo_pago} · {inv.forma_pago_label} · {inv.uso_cfdi_label}{inv.concepto ? ` · ${inv.concepto}` : ""}</div>
                                {inv.fecha_vencimiento && <div className="text-xs text-amber-700 mt-0.5">Vence: {fmtDate(inv.fecha_vencimiento)}{inv.paid_at ? ` · Pagada ${fmtDate(inv.paid_at)}` : ""}</div>}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => preview(inv)} data-testid={`preview-${inv.id}`}><FileText className="w-3.5 h-3.5 mr-1" /> Preliminar</Button>
                              {inv.estatus === "borrador" && <>
                                <FileBtn inv={inv} kind="pdf" endpoint="attach" label={inv.pdf_file_id ? "PDF ✓" : "Adjuntar PDF"} done={!!inv.pdf_file_id} />
                                <FileBtn inv={inv} kind="xml" endpoint="attach" label={inv.xml_file_id ? "XML ✓" : "Adjuntar XML"} done={!!inv.xml_file_id} />
                                <Button size="sm" disabled={!inv.pdf_file_id || !inv.xml_file_id} onClick={() => sendInvoice(inv)} data-testid={`send-${inv.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Enviar factura</Button>
                                <Button variant="ghost" size="sm" onClick={() => delInvoice(inv)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                              </>}
                              {inv.estatus !== "borrador" && <>
                                {inv.pdf_file_id && <a href={`${BACKEND}/api/files/${inv.pdf_file_id}`} target="_blank" rel="noreferrer" className="text-xs underline text-interactive">PDF timbrado</a>}
                                {inv.xml_file_id && <a href={`${BACKEND}/api/files/${inv.xml_file_id}`} target="_blank" rel="noreferrer" className="text-xs underline text-interactive">XML timbrado</a>}
                              </>}
                              {inv.estatus !== "borrador" && inv.metodo_pago === "PUE" && inv.estatus !== "pagada" && (
                                <Button size="sm" onClick={() => markPaid(inv)} data-testid={`mark-paid-${inv.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marcar pagada (PUE)</Button>
                              )}
                              {inv.estatus !== "borrador" && inv.metodo_pago !== "PUE" && <>
                                <span className="text-xs text-muted-foreground flex items-center gap-1"><CircleDollarSign className="w-3.5 h-3.5" /> Complemento de pago:</span>
                                <FileBtn inv={inv} kind="pdf" endpoint="complemento" label={inv.complemento_pdf_id ? "PDF ✓" : "Compl. PDF"} done={!!inv.complemento_pdf_id} />
                                <FileBtn inv={inv} kind="xml" endpoint="complemento" label={inv.complemento_xml_id ? "XML ✓" : "Compl. XML"} done={!!inv.complemento_xml_id} />
                                <Button size="sm" disabled={!inv.complemento_pdf_id || !inv.complemento_xml_id} onClick={() => sendComplemento(inv)} data-testid={`send-complemento-${inv.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Enviar complemento</Button>
                                {inv.complemento_sent_at && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Enviado {fmtDate(inv.complemento_sent_at)}</span>}
                              </>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay pedidos por facturar. Los pedidos aparecen aquí al cerrarse.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!dlgOrder} onOpenChange={(v) => !v && setDlgOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Crear factura {dlgOrder && `· Pedido ${dlgOrder.folio}`}</DialogTitle></DialogHeader>
          {dlgOrder && <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Método de pago</Label>
                <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                  <SelectTrigger data-testid="inv-metodo"><SelectValue /></SelectTrigger>
                  <SelectContent>{cat.metodos_pago.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Forma de pago (SAT)</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                  <SelectTrigger data-testid="inv-forma"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">{cat.formas_pago.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Uso del CFDI (SAT)</Label>
              <Select value={form.uso_cfdi} onValueChange={(v) => setForm({ ...form, uso_cfdi: v })}>
                <SelectTrigger data-testid="inv-uso"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{cat.usos_cfdi.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Concepto (opcional)</Label><Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="Ej. Entrega parcial / servicio" data-testid="inv-concepto" /></div>
            <div className="flex items-center justify-between border rounded-lg px-3 py-2"><div className="text-sm">Facturación parcial</div><Switch checked={form.is_partial} onCheckedChange={(v) => setForm({ ...form, is_partial: v })} data-testid="inv-partial" /></div>
            {form.is_partial ? (
              <div className="border rounded-lg divide-y" data-testid="inv-items">
                <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/40 grid grid-cols-12 gap-2"><span className="col-span-6">Ítem</span><span className="col-span-2 text-right">Pendiente</span><span className="col-span-4 text-right">Cant. a facturar</span></div>
                {(form.lineas || []).map((l, idx) => (
                  <div key={idx} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-sm">
                    <span className="col-span-6">{l.nombre}<div className="text-xs text-muted-foreground">{fmtMXN(l.precio_unitario)} c/u</div></span>
                    <span className="col-span-2 text-right">{l.restante}</span>
                    <span className="col-span-4"><Input type="number" min={0} max={l.restante} value={l.cantidad_fac} disabled={l.restante <= 0} onChange={(e) => setLine(idx, e.target.value)} data-testid={`inv-item-${idx}`} className="h-8 text-right" /></span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Se facturará el <b>saldo completo</b> del pedido: {fmtMXN(dlgOrder.saldo_facturar)} de {fmtMXN(dlgOrder.total)}.</p>
            )}
            <div className="text-right text-sm">Total a facturar (con IVA): <b data-testid="inv-total">{fmtMXN(calcMonto())}</b></div>
          </div>}
          <DialogFooter><Button onClick={createInvoice} data-testid="save-invoice">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
